import React, { useState, useEffect, useMemo } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Megaphone, DollarSign, TrendingUp, XCircle, PlayCircle, PauseCircle, CheckSquare, Trash2, Tag, CheckCircle, FileText, Save, PlusCircle } from 'lucide-react';
import Swal from 'sweetalert2';

const MarketingDashboard = () => {
    const [activeTab, setActiveTab] = useState('publicidad'); // 'publicidad', 'estrategias', 'checklist', 'bitacora'
    const [campanas, setCampanas] = useState([]);
    const [estrategias, setEstrategias] = useState([]);
    
    const [showModal, setShowModal] = useState(false);
    const [showEstrategiaModal, setShowEstrategiaModal] = useState(false);
    
    // Additional State
    const [rutinasMarketing, setRutinasMarketing] = useState([]);
    const [checklistDia, setChecklistDia] = useState({});
    const [bitacora, setBitacora] = useState('');

    // Form fields Campañas
    const [formNombre, setFormNombre] = useState('');
    const [formMedio, setFormMedio] = useState('');
    const [formInversion, setFormInversion] = useState('');
    const [formFechaInicio, setFormFechaInicio] = useState('');
    const [formFechaFin, setFormFechaFin] = useState('');
    const [formEstado, setFormEstado] = useState('Activa');

    // Form fields Estrategias
    const [estNombre, setEstNombre] = useState('');
    const [estDescripcion, setEstDescripcion] = useState('');
    const [estAlcance, setEstAlcance] = useState('');
    const [estEstado, setEstEstado] = useState('Activa');

    useEffect(() => {
        const q = query(collection(db, 'marketing_campanas'), orderBy('fecha_inicio', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            setCampanas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const qEstrategias = query(collection(db, 'marketing_estrategias'), orderBy('createdAt', 'desc'));
        const unsubEstrategias = onSnapshot(qEstrategias, (snap) => setEstrategias(snap.docs.map(d => ({id: d.id, ...d.data()}))));

        const qRutinas = query(collection(db, 'config_rutinas'), orderBy('orden', 'asc'));
        const unsubRutinas = onSnapshot(qRutinas, (snap) => setRutinasMarketing(snap.docs.map(d => ({id: d.id, ...d.data()})).filter(r => r.departamento === 'Marketing')));


        const todayId = new Date().toISOString().slice(0, 10);
        const unsubChecklist = onSnapshot(doc(db, 'marketing_checklist_logs', todayId), (docSnap) => {
            if (docSnap.exists()) {
                setChecklistDia(docSnap.data().checks || {});
            } else {
                setChecklistDia({});
            }
        });

        const unsubBitacora = onSnapshot(doc(db, 'marketing_notas', 'mes_actual'), (docSnap) => {
            if (docSnap.exists()) {
                setBitacora(docSnap.data().texto || '');
            }
        });

        return () => { unsub(); unsubEstrategias(); unsubRutinas(); unsubChecklist(); unsubBitacora(); };
    }, []);

    const handleBitacoraChange = (e) => setBitacora(e.target.value);

    const handleSaveBitacora = async () => {
        await setDoc(doc(db, 'marketing_notas', 'mes_actual'), { texto: bitacora });
        Swal.fire({ title: 'Notas Guardadas', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
    };

    const handleSaveCampana = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'marketing_campanas'), {
                nombre: formNombre,
                medio: formMedio,
                inversion: Number(formInversion),
                fecha_inicio: new Date(formFechaInicio + 'T12:00:00Z').getTime(),
                fecha_fin: new Date(formFechaFin + 'T12:00:00Z').getTime(),
                estado: formEstado,
                createdAt: serverTimestamp()
            });
            setShowModal(false);
            // Reset form
            setFormNombre(''); setFormMedio(''); setFormInversion('');
            setFormFechaInicio(''); setFormFechaFin(''); setFormEstado('Activa');
            Swal.fire({ title: 'Guardado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar la campaña', 'error');
        }
    };

    const handleStatusChange = async (id, nuevoEstado) => {
        try {
            await updateDoc(doc(db, 'marketing_campanas', id), { estado: nuevoEstado });
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if(window.confirm('¿Eliminar definitivamente este registro?')) {
            await deleteDoc(doc(db, 'marketing_campanas', id));
        }
    };

    const toggleChecklist = async (rutinaId, isChecked) => {
        const todayId = new Date().toISOString().slice(0, 10);
        const newChecks = { ...checklistDia, [rutinaId]: isChecked };
        try {
            await setDoc(doc(db, 'marketing_checklist_logs', todayId), { checks: newChecks }, { merge: true });
        } catch(error) {
            console.error("Error saving checklist:", error);
        }
    };

    const handleSaveEstrategia = async (e) => {
        e.preventDefault();
        try {
            await addDoc(collection(db, 'marketing_estrategias'), {
                nombre: estNombre,
                descripcion: estDescripcion,
                alcance: estAlcance,
                estado: estEstado,
                createdAt: serverTimestamp()
            });
            setShowEstrategiaModal(false);
            setEstNombre(''); setEstDescripcion(''); setEstAlcance(''); setEstEstado('Activa');
            Swal.fire({ title: 'Guardado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch(error) {
            Swal.fire('Error', 'No se pudo guardar la estrategia', 'error');
        }
    };

    const handleStatusEstrategia = async (id, nuevoEstado) => {
        await updateDoc(doc(db, 'marketing_estrategias', id), { estado: nuevoEstado });
    };

    const handleDeleteEstrategia = async (id) => {
        if(window.confirm('¿Eliminar estrategia?')) {
            await deleteDoc(doc(db, 'marketing_estrategias', id));
        }
    };

    // Derived State
    const resumen = useMemo(() => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        let inv = 0;
        let activas = 0;
        let promos = 0;

        campanas.forEach(c => {
            const d = new Date(c.fecha_inicio);
            if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
                inv += c.inversion || 0;
            }
            if (c.estado === 'Activa') activas++;
        });
        estrategias.forEach(e => {
            if (e.estado === 'Activa') promos++;
        });
        return { inversionMes: inv, campañasActivas: activas, promocionesVigentes: promos };
    }, [campanas, estrategias]);

    const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString() : 'N/A';

    const getStatusBadge = (estado) => {
        switch(estado) {
            case 'Activa': return <span className="bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-md text-xs font-bold uppercase">Activa</span>;
            case 'Pausada': return <span className="bg-amber-500/10 text-amber-400 px-2 py-1 rounded-md text-xs font-bold uppercase">Pausada</span>;
            case 'Finalizada': return <span className="bg-slate-500/10 text-slate-400 px-2 py-1 rounded-md text-xs font-bold uppercase">Finalizada</span>;
            default: return null;
        }
    };

    return (
        <div className="min-h-screen text-slate-100 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight m-0">Marketing</h1>
                    <p className="text-sm text-slate-400 mt-1">Gestión de campañas publicitarias y estrategias</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setShowModal(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg shadow-indigo-900/50">
                        <Megaphone size={18} /> Nueva Campaña
                    </button>
                    <button onClick={() => setShowEstrategiaModal(true)} className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg shadow-amber-900/50">
                        <PlusCircle size={18} /> Nueva Estrategia
                    </button>
                </div>
            </div>

            {/* CARDS DE RESUMEN */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Inversión del Mes</span>
                        <div className="w-8 h-8 rounded bg-indigo-500/20 text-indigo-500 flex items-center justify-center">
                            <DollarSign size={18} />
                        </div>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">${resumen.inversionMes.toLocaleString()}</div>
                    <div className="text-xs text-indigo-400 flex items-center gap-1">Gasto en publicidad pagada</div>
                </div>
                
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Campañas Activas</span>
                        <div className="w-8 h-8 rounded bg-emerald-500/20 text-emerald-500 flex items-center justify-center">
                            <TrendingUp size={18} />
                        </div>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{resumen.campañasActivas}</div>
                    <div className="text-xs text-emerald-400 flex items-center gap-1">Generando tráfico</div>
                </div>

                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Estrategias Activas</span>
                        <div className="w-8 h-8 rounded bg-amber-500/20 text-amber-500 flex items-center justify-center">
                            <Tag size={18} />
                        </div>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">{resumen.promocionesVigentes}</div>
                    <div className="text-xs text-amber-400 flex items-center gap-1">Estrategias en curso</div>
                </div>
            </div>

            {/* TABS */}
            <div className="flex border-b border-slate-700/50 mb-6 gap-2 overflow-x-auto">
                <button onClick={() => setActiveTab('publicidad')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${activeTab === 'publicidad' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <Megaphone size={18} /> Campañas Pagadas
                </button>
                <button onClick={() => setActiveTab('estrategias')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${activeTab === 'estrategias' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <Tag size={18} /> Estrategias Activas
                </button>
                <button onClick={() => setActiveTab('checklist')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${activeTab === 'checklist' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <CheckCircle size={18} /> Checklist Diario
                </button>
                <button onClick={() => setActiveTab('bitacora')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${activeTab === 'bitacora' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <FileText size={18} /> Bitácora del Mes
                </button>
            </div>

            {/* TAB: PUBLICIDAD PAGADA */}
            {activeTab === 'publicidad' && (
                <div className="animate-in fade-in duration-300 bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-slate-300">
                            <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                <tr>
                                    <th className="px-6 py-4">Campaña</th>
                                    <th className="px-6 py-4">Medio</th>
                                    <th className="px-6 py-4 font-bold text-indigo-400">Inversión</th>
                                    <th className="px-6 py-4">Fechas</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {campanas.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-8 text-slate-500">No hay publicidad pagada registrada</td></tr>
                                ) : campanas.map(c => (
                                    <tr key={c.id} className="hover:bg-slate-700/30 transition-colors">
                                        <td className="px-6 py-4 font-bold text-white">{c.nombre}</td>
                                        <td className="px-6 py-4">{c.medio}</td>
                                        <td className="px-6 py-4 font-mono text-indigo-400">${c.inversion}</td>
                                        <td className="px-6 py-4 text-xs text-slate-400">
                                            {formatDate(c.fecha_inicio)} - {formatDate(c.fecha_fin)}
                                        </td>
                                        <td className="px-6 py-4">{getStatusBadge(c.estado)}</td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleStatusChange(c.id, 'Pausada')} title="Pausar" className="text-slate-400 hover:text-amber-400 p-1"><PauseCircle size={18} /></button>
                                                <button onClick={() => handleStatusChange(c.id, 'Finalizada')} title="Finalizar" className="text-slate-400 hover:text-slate-300 p-1"><CheckSquare size={18} /></button>
                                                <button onClick={() => handleDelete(c.id)} title="Eliminar" className="text-slate-400 hover:text-red-400 p-1"><Trash2 size={18} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB: ESTRATEGIAS ACTIVAS */}
            {activeTab === 'estrategias' && (
                <div className="animate-in fade-in duration-300 bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-slate-300">
                            <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                <tr>
                                    <th className="px-6 py-4">Estrategia</th>
                                    <th className="px-6 py-4">Descripción</th>
                                    <th className="px-6 py-4">Alcance</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {estrategias.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center py-8 text-slate-500">No hay estrategias activas</td></tr>
                                ) : estrategias.map(e => (
                                    <tr key={e.id} className="hover:bg-slate-700/30 transition-colors">
                                        <td className="px-6 py-4 font-bold text-amber-400">{e.nombre}</td>
                                        <td className="px-6 py-4">{e.descripcion || 'N/A'}</td>
                                        <td className="px-6 py-4">{e.alcance || 'N/A'}</td>
                                        <td className="px-6 py-4">{getStatusBadge(e.estado)}</td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleStatusEstrategia(e.id, 'Activa')} title="Activar" className="text-slate-400 hover:text-emerald-400 p-1"><PlayCircle size={18} /></button>
                                                <button onClick={() => handleStatusEstrategia(e.id, 'Pausada')} title="Pausar" className="text-slate-400 hover:text-amber-400 p-1"><PauseCircle size={18} /></button>
                                                <button onClick={() => handleStatusEstrategia(e.id, 'Finalizada')} title="Finalizar" className="text-slate-400 hover:text-slate-300 p-1"><CheckSquare size={18} /></button>
                                                <button onClick={() => handleDeleteEstrategia(e.id)} title="Eliminar" className="text-slate-400 hover:text-red-400 p-1"><Trash2 size={18} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB: CHECKLIST DIARIO */}
            {activeTab === 'checklist' && (
                <div className="animate-in fade-in duration-300 bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Mi Checklist Diario (Marketing)</h2>
                    <div className="space-y-4">
                        {rutinasMarketing.length === 0 ? <p className="text-slate-500">No hay rutinas asignadas al departamento de Marketing.</p> : rutinasMarketing.map(r => (
                            <label key={r.id} className={`flex items-center gap-3 p-4 bg-slate-900/50 border border-slate-700 rounded-xl cursor-pointer hover:border-emerald-500/50 transition-colors ${checklistDia[r.id] ? 'opacity-60' : ''}`}>
                                <input 
                                    type="checkbox" 
                                    checked={checklistDia[r.id] || false}
                                    onChange={(e) => toggleChecklist(r.id, e.target.checked)}
                                    className="w-5 h-5 rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-0" 
                                />
                                <span className={`text-slate-300 font-medium ${checklistDia[r.id] ? 'line-through text-slate-500' : ''}`}>{r.tarea}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* TAB: BITÁCORA DEL MES */}
            {activeTab === 'bitacora' && (
                <div className="animate-in fade-in duration-300 bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl p-6 flex flex-col h-[500px]">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-white flex items-center gap-2"><FileText size={20} className="text-blue-500" /> Bitácora Mensual</h2>
                        <span className="text-xs text-slate-500 bg-slate-900 px-3 py-1 rounded-full border border-slate-700">Guardado Automático</span>
                    </div>
                    <div className="flex-1 flex flex-col gap-4">
                        <textarea 
                            value={bitacora} 
                            onChange={handleBitacoraChange} 
                            className="flex-1 w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-slate-300 focus:outline-none focus:border-blue-500 resize-none font-mono text-sm" 
                            placeholder="Escribe notas de reuniones, observaciones de campañas, o ideas para el próximo mes..."
                        ></textarea>
                        <div className="flex justify-end">
                            <button onClick={handleSaveBitacora} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg shadow-blue-900/50">
                                <Save size={18} /> Guardar Notas
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: NUEVA CAMPAÑA/PROMO */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white">Registrar Campaña Pagada</h3>
                            <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><XCircle size={24} /></button>
                        </div>
                        <form onSubmit={handleSaveCampana} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Nombre</label>
                                <input required type="text" value={formNombre} onChange={(e) => setFormNombre(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none" placeholder="Ej: Meta Ads Conversión" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Medio / Canal</label>
                                <input required type="text" value={formMedio} onChange={(e) => setFormMedio(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none" placeholder="Ej: Instagram, Google Ads" />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Inversión (USD)</label>
                                <input required type="number" min="0" value={formInversion} onChange={(e) => setFormInversion(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none" placeholder="50" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Fecha Inicio</label>
                                    <input required type="date" value={formFechaInicio} onChange={(e) => setFormFechaInicio(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none [color-scheme:dark]" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Fecha Fin</label>
                                    <input required type="date" value={formFechaFin} onChange={(e) => setFormFechaFin(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none [color-scheme:dark]" />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Estado Inicial</label>
                                <select value={formEstado} onChange={(e) => setFormEstado(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none">
                                    <option value="Activa">Activa</option>
                                    <option value="Pausada">Pausada</option>
                                    <option value="Finalizada">Finalizada</option>
                                </select>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors flex justify-center items-center gap-2">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: NUEVA ESTRATEGIA */}
            {showEstrategiaModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white">Registrar Estrategia</h3>
                            <button onClick={() => setShowEstrategiaModal(false)} className="text-slate-400 hover:text-white"><XCircle size={24} /></button>
                        </div>
                        <form onSubmit={handleSaveEstrategia} className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Nombre</label>
                                <input required type="text" value={estNombre} onChange={(e) => setEstNombre(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none" placeholder="Ej: Promo 2x1 Max" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Descripción</label>
                                <textarea value={estDescripcion} onChange={(e) => setEstDescripcion(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none resize-none h-24" placeholder="Describe brevemente la estrategia..."></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Alcance / Objetivo</label>
                                <input type="text" value={estAlcance} onChange={(e) => setEstAlcance(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none" placeholder="Ej: Atraer clientes antiguos" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Estado Inicial</label>
                                <select value={estEstado} onChange={(e) => setEstEstado(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-amber-500 focus:outline-none">
                                    <option value="Activa">Activa</option>
                                    <option value="Pausada">Pausada</option>
                                    <option value="Finalizada">Finalizada</option>
                                </select>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setShowEstrategiaModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors flex justify-center items-center gap-2">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketingDashboard;
